import { useState, type FormEvent, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { roleAtLeast } from '@os/shared';
import { ApiError, type BoardAccess } from '../../lib/api';
import type { BoardVersion } from '../../lib/version-types';
import { Button, Input, ListItem, Panel } from '../../ui';
import { canRestoreVersions } from '../sharing/capabilities';
import { groupByDay, versionTitle } from './labels';
import { useVersions } from './useVersions';

type Props = { boardId: string; access: BoardAccess; onPreview: (v: BoardVersion) => void; onClose: () => void; toggle?: RefObject<HTMLElement | null> };

/**
 * The board's versions (spec §11.1), newest first by day: editors name the current state, a row opens the read-only
 * preview, a manual version can be deleted by those who may restore (§9.2).
 */
export function HistoryPanel({ boardId, access, onPreview, onClose, toggle }: Props) {
  const { t, i18n } = useTranslation();
  const { list, create, remove } = useVersions(boardId);
  const [label, setLabel] = useState('');
  const [confirming, setConfirming] = useState<string | null>(null);
  const canCreate = roleAtLeast(access.role, 'editor');
  const canDelete = canRestoreVersions(access);
  const time = new Intl.DateTimeFormat(i18n.language, { timeStyle: 'short' });
  const createError = create.error instanceof ApiError && create.error.code === 'limit' ? t('history.limit') : create.error ? t('history.error') : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const clean = label.trim();
    if (!clean) return;
    create.mutate(clean, { onSuccess: () => setLabel('') });
  };

  return (
    <Panel title={t('history.title')} closeLabel={t('history.close')} onClose={onClose} toggle={toggle} placement="right" className="top-24 bottom-20 gap-3 max-md:inset-x-3 max-md:w-auto">
      {canCreate && (
        <form onSubmit={submit} className="flex flex-col gap-2">
          <Input aria-label={t('history.nameLabel')} placeholder={t('history.namePlaceholder')} value={label} maxLength={200} onChange={(e) => setLabel(e.target.value)} />
          <Button type="submit" variant="primary" disabled={!label.trim() || create.isPending}>
            {t('history.save')}
          </Button>
          {createError && (
            <p role="alert" className="font-sans text-xs text-danger">
              {createError}
            </p>
          )}
        </form>
      )}
      {list.isError ? (
        <p role="alert" className="font-sans text-sm text-fg-muted">
          {t('history.loadError')}
        </p>
      ) : list.data && list.data.length === 0 ? (
        <p className="font-sans text-sm text-fg-muted">{t('history.empty')}</p>
      ) : (
        <div className="flex flex-col gap-3 overflow-auto">
          {groupByDay(list.data ?? [], i18n.language).map((g) => (
            <section key={g.day} aria-label={g.day} className="flex flex-col gap-1">
              <h3 className="font-sans text-xs font-medium uppercase text-fg-muted">{g.day}</h3>
              <ul className="flex flex-col gap-1">
                {g.items.map((v) => (
                  <li key={v.id} className="flex items-center gap-2">
                    <ListItem
                      onClick={() => onPreview(v)}
                      className="h-auto min-h-11 flex-1 py-1.5"
                      label={
                        <span className="flex min-w-0 flex-col">
                          <span className={`truncate ${v.kind === 'manual' ? 'font-medium text-fg' : 'text-fg-muted'}`}>{versionTitle(v, t)}</span>
                          <span className="text-xs text-fg-muted">
                            {time.format(new Date(v.createdAt))}
                            {v.createdByName && ` · ${t('history.by', { name: v.createdByName })}`}
                          </span>
                        </span>
                      }
                    />
                    {canDelete && v.kind === 'manual' &&
                      (confirming === v.id ? (
                        <Button size="sm" variant="danger" onClick={() => remove.mutate(v.id, { onSettled: () => setConfirming(null) })}>
                          {t('history.confirmDelete')}
                        </Button>
                      ) : (
                        <Button size="sm" onClick={() => setConfirming(v.id)}>
                          {t('history.delete')}
                        </Button>
                      ))}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </Panel>
  );
}
